import { ColorThemeType } from '@qlevr/shared/constants';
import {
  CardItemInterface,
  ImageInterface,
  NavigationItemInterface,
  NavigationListItemInterface,
  SubMenuTypeListInterface,
  TextImageCardInterface,
} from '@qlevr/shared/interfaces';
import {
  CardItemStoryblok,
  MenuStoryblok,
  MenuTypeListStoryblok,
  ShopifyProductStoryblok,
  SubMenuTypeListStoryblok,
  TextImageCardStoryblok,
} from '@qlevr/shared/schema';
import isEmpty from 'lodash.isempty';
import { mapTextImageCard } from '../card/text-image-card-mapper';
import { linkMapper } from '../link/link';
import { themeMapper } from '../theme/theme';
import { imageStoryblokMapper } from '../image/image-storyblok';

/**
 *
 * IMPORTANT
 * Part of the data will be mapped once we have received additional data from Shopify.
 * @see mapNavigationProduct()
 */
export function mapNavigationItem(
  navigation?: (MenuStoryblok | MenuTypeListStoryblok)[],
): (NavigationItemInterface | NavigationListItemInterface)[] {
  if (!navigation) {
    return [];
  }

  return (
    navigation.reduce(
      (acc, item) => {
        const link = linkMapper(item?.link?.[0]);
        const theme = themeMapper(item.theme?.toString());
        const mobileTheme = themeMapper(item.mobileTheme?.toString() ?? item.theme?.toString());

        if (!link) return acc;

        if (isMenuStoryblok(item)) {
          acc.push({
            link,
            featuredProductId: mapFeaturedProductId(item['featuredProduct']?.items?.[0]),
            featuredProductVariantKey: mapFeaturedVariantKey(item.featuredProductVariantKey),
            stackedProductsOnMobile: item.stackedProductsOnMobile ?? false,
            featuredCard: mapFeaturedCard(item.textImageCard?.[0], theme),
            featuredProductCtaLabel: item.featuredProductCtaLabel ?? '',
            products: null,
            productIds: mapProductIds(item?.['products']?.items),
            theme,
            mobileTheme,
          });
        } else if (isMenuTypeListStoryblok(item)) {
          acc.push({
            link,
            subMenu: mapSubMenu(item.subMenu),
            theme,
          });
        }

        return acc;
      },
      [] as (NavigationItemInterface | NavigationListItemInterface)[],
    ) ?? []
  );
}

export function mapSubMenu(subMenu?: SubMenuTypeListStoryblok[]): SubMenuTypeListInterface[] {
  if (!subMenu || isEmpty(subMenu)) {
    return [];
  }

  return (
    subMenu.reduce((acc, item) => {
      const link = linkMapper(item.mainLink[0]);
      const viewAllLink = linkMapper(item?.viewAll?.[0]) ?? null;
      const subMenuItems = mapCardItem(item?.subMenuItems ?? []);

      acc.push({
        link,
        viewAllLink,
        subMenuItems,
      });
      return acc;
    }, [] as SubMenuTypeListInterface[]) ?? []
  );
}

export function mapCardItem(cards: CardItemStoryblok[]): CardItemInterface[] {
  if (!cards || isEmpty(cards)) {
    return [];
  }

  return (
    cards.reduce((acc, item) => {
      const cta = linkMapper(item.cta[0]);
      const image = imageStoryblokMapper(item.image[0], { xs: 1 }, false) as ImageInterface;

      if (!cta || !image) return acc;

      acc.push({
        cta,
        image,
      });

      return acc;
    }, [] as CardItemInterface[]) ?? []
  );
}

/**
 * Type Guards to differentiate between MenuStoryblok and MenuTypeListStoryblok
 */
function isMenuStoryblok(item: MenuStoryblok | MenuTypeListStoryblok): item is MenuStoryblok {
  return 'featuredProduct' in item || 'featuredProductVariantKey' in item;
}

function isMenuTypeListStoryblok(item: MenuStoryblok | MenuTypeListStoryblok): item is MenuTypeListStoryblok {
  return 'subMenu' in item;
}

export function mapProductIds(products: ShopifyProductStoryblok[]): string[] {
  if (!products || isEmpty(products)) {
    return [];
  }

  return products.flatMap((product: ShopifyProductStoryblok) => product.id) ?? [];
}

export function mapFeaturedCard(
  textImageCard: TextImageCardStoryblok | undefined,
  theme: ColorThemeType,
): TextImageCardInterface | null {
  if (!textImageCard) {
    return null;
  }

  return mapTextImageCard(textImageCard, theme);
}

export function mapFeaturedProductId(product: ShopifyProductStoryblok): string | null {
  if (!product?.id) {
    return null;
  }

  return product.id;
}

export function mapFeaturedVariantKey(variantKey?: string): string | null {
  if (!variantKey || isEmpty(variantKey)) {
    return null;
  }

  return variantKey;
}
